<script lang="ts">
  export let label: string;
  export let value: string | number;
  export let helper: string | undefined = undefined;
  export let status: 'ok' | 'warning' | 'error' | 'neutral' = 'neutral';
  /** Quando definido, o tile fica clicável e despacha evento 'drilldown'. */
  export let drillDownId: string | undefined = undefined;

  /**
   * Quebra valores que carregam um detalhe em parênteses (ex.: "2614 (449
   * colaboradores)") em duas peças visuais — número grande + qualificador
   * pequeno — pra evitar que o texto inteiro tenha que caber em 2rem na
   * largura do tile (180px) e estoure o card vizinho.
   */
  function splitValue(v: string | number): { primary: string; secondary?: string } {
    if (typeof v !== 'string') return { primary: String(v) };
    const match = v.match(/^(.+?)\s*\((.+)\)\s*$/);
    if (match) return { primary: match[1]!, secondary: match[2]! };
    return { primary: v };
  }

  $: parts = splitValue(value);
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
  class="metric-tile metric-tile--{status}"
  class:metric-tile--clickable={drillDownId !== undefined}
  role={drillDownId !== undefined ? 'button' : undefined}
  tabindex={drillDownId !== undefined ? 0 : undefined}
  on:click
  on:keydown={(e) => {
    if (drillDownId !== undefined && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.currentTarget.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    }
  }}
>
  <span class="metric-tile__value">{parts.primary}</span>
  {#if parts.secondary}
    <span class="metric-tile__secondary">({parts.secondary})</span>
  {/if}
  <span class="metric-tile__label">{label}</span>
  {#if helper}
    <span class="metric-tile__helper">{helper}</span>
  {/if}
  {#if drillDownId !== undefined}
    <span class="metric-tile__drill-hint" aria-hidden="true">Ver detalhes →</span>
  {/if}
</div>

<style>
  .metric-tile {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
    padding: var(--ptt-space-4) var(--ptt-space-5);
    background: var(--ptt-gray-10);
    border-radius: var(--ptt-radius-md);
    border-left: 4px solid transparent;
    /* Sem min-width:0, grid items respeitam o tamanho intrínseco do conteúdo
       (texto longo) e estouram a coluna, sobrepondo o card vizinho. */
    min-width: 0;
  }

  .metric-tile--clickable {
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease;
  }

  .metric-tile--clickable:hover {
    background: var(--ptt-gray-20, #e7e7e7);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .metric-tile--clickable:focus-visible {
    outline: 2px solid var(--ptt-purple-100, #5B2FD4);
    outline-offset: 2px;
  }

  .metric-tile--ok {
    border-left-color: var(--ptt-green-100);
  }

  .metric-tile--warning {
    border-left-color: var(--ptt-yellow-200);
  }

  .metric-tile--error {
    border-left-color: var(--ptt-orange-100);
  }

  .metric-tile--neutral {
    border-left-color: var(--ptt-gray-20);
  }

  .metric-tile__value {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.1;
    color: var(--ptt-gray-300);
    overflow-wrap: break-word;
    word-break: break-word;
    /* Permite que números muito longos quebrem ao invés de transbordar pro card vizinho. */
    min-width: 0;
  }

  .metric-tile__secondary {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--ptt-gray-200);
    line-height: 1.2;
    margin-top: -2px;
  }

  .metric-tile__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-gray-200);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .metric-tile__helper {
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
    margin-top: var(--ptt-space-1);
  }

  .metric-tile__drill-hint {
    font-size: 0.7rem;
    color: var(--ptt-purple-100, #5B2FD4);
    margin-top: var(--ptt-space-1);
    font-weight: 600;
    letter-spacing: 0.02em;
  }
</style>
